import { MouseEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { scrollToSection } from '../layout/OnThisPage';
import { OnThisPageItem } from '../../types/onThisPage';
import { programHubHref } from '../../utils/hubChapter';
import { followPlaceStep, readPlaceSurface } from '../../utils/placeMotion';

export interface ProgramSectionHub {
  title: string;
  slug: string;
  resourceCount: number;
}

export function visibleProgramSections(
  hubs: ProgramSectionHub[] | undefined
): ProgramSectionHub[] {
  const withDocuments = (hubs ?? []).filter((hub) => hub.resourceCount > 0);
  return withDocuments.length > 1 ? withDocuments : [];
}

interface ProgramSectionListProps {
  program: { title: string; slug: string };
  hubs: ProgramSectionHub[] | undefined;
  currentSlug?: string;
  className?: string;
  pageSections?: OnThisPageItem[];
  activeSectionId?: string | null;
}

export default function ProgramSectionList({
  program,
  hubs,
  currentSlug,
  className = 'mb-8',
  pageSections,
  activeSectionId = null,
}: ProgramSectionListProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const surface = readPlaceSurface(location.state);
  const sections = visibleProgramSections(hubs);
  if (sections.length === 0) return null;

  const takeStep = (event: MouseEvent<HTMLAnchorElement>, step: 'shallower' | 'deeper', to: string) => {
    followPlaceStep(event, step, to, navigate, location, surface);
  };

  return (
    <nav aria-label="In this program" className={className}>
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
        In this program
      </p>
      <ul className="flex flex-col">
        {sections.map((item) => {
          const current = item.slug === currentSlug;
          return (
            <li key={item.slug}>
              {current ? (
                <>
                  <span
                    aria-current="page"
                    className="flex min-h-[44px] items-center py-2 text-sm font-medium text-gray-900"
                  >
                    {item.title}
                  </span>
                  {pageSections && pageSections.length > 0 ? (
                    <ul className="mb-2 ml-3 flex flex-col border-l border-gray-200">
                      {pageSections.map((section) => {
                        const active = section.id === activeSectionId;
                        return (
                          <li key={section.id}>
                            <a
                              href={`#${section.id}`}
                              className={`flex min-h-[44px] items-center py-2 pl-3 text-sm transition-colors ${
                                active
                                  ? 'font-medium text-primary'
                                  : 'text-gray-700 hover:text-primary'
                              }`}
                              onClick={(event) => scrollToSection(event, section.id)}
                            >
                              {section.label}
                            </a>
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </>
              ) : (
                <Link
                  to={programHubHref(program.slug, item.slug)}
                  onClick={(event) => takeStep(event, 'deeper', programHubHref(program.slug, item.slug))}
                  className="flex min-h-[44px] items-center py-2 text-sm text-gray-700 hover:text-primary transition-colors"
                >
                  {item.title}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
